import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ChevronRight, ColoredChevronLeft, GreenTick, MonoLogo } from "../../../../assets/icons";
import Button, { ButtonStyle } from "../../../../common/components/button/button";
import CommonCheckBox from "../../../../common/components/checkbox/CommonCheckBox";
import styles from "./payment.module.css";
import ConfirmationDialog from "../../../../common/components/dialog/ConfirmationDialog";
import { useView } from "../provider/ViewContext";
import { useAuthorizeCardTransactionMutation } from "../../../../service/paystack-service";
import { useDispatch } from "react-redux";
import { hideLoader, showLoader } from "../../../../stores/slices/loader-slice";
import { toast } from "react-toastify";
import { AUTHORIZATION_STATUS } from "../constant";
import { useLocation } from "react-router-dom";
import { useInitiateDebitMandateMutation } from "../../../../service/direct-debit-service";
import { useLazyCheckMandateConnectedQuery } from "../../../../service/order-service";
import { ApplicationRoutes } from "../../../../routes";

const AuthorizePaymentMethod = () => {
	const dispatch = useDispatch();
	const location = useLocation();
	const navigate = useNavigate();
	const [isAcceptTerms, setIsAcceptTerms] = useState(false);
	const [isConnectCard, setIsConnectCards] = useState(false);
	const [isConnectMono, setIsConnectMono] = useState(false);
	const [isCardAlreadyConnected, setIsCardAlreadyConnected] = useState(false);
	const [isMonoAlreadyConnected, setIsMonoAlreadyConnected] = useState(false);
	const [showConfirm, setShowConfirm] = useState(false);
	const { setView, orderId } = useView();
	const [triggerCardTransaction] = useAuthorizeCardTransactionMutation();
	const [triggerDebitMandate] = useInitiateDebitMandateMutation();
	const [checkMandateStatus] = useLazyCheckMandateConnectedQuery();

	useEffect(() => {
		if (orderId) {
			handleCheckMandateConnceted();
		}
	}, [orderId]);

	useEffect(() => {
		const params = new URLSearchParams(location.search);
		const status = params.get("status");
		const reason = params.get("reason");
		if (status === AUTHORIZATION_STATUS.CARD_SUCESS) {
			toast.success("Card authorization successful!");
			setIsConnectCards(true);
		} else if (status === AUTHORIZATION_STATUS.CARD_FAILURE) {
			toast.error("Unable to authorize card!");
		} else if (status === AUTHORIZATION_STATUS.DEBIT_SUCCESS) {
			toast.success("Debit Mandate Authorized Successful!");
			setIsConnectMono(true);
		} else if (status) {
			setIsAcceptTerms(true);
			toast.error(`Process failed: ${reason?? 'try again!'}`);
		}
		window.history.replaceState(null, "", "/customer/dashboard");
	}, [location]);

	const handleInitateCardTransaction = () => {
		if (!isAcceptTerms) {
			toast.warning("You have to accept the terms");
			return;
		}
		dispatch(showLoader());
		triggerCardTransaction({
			redirectUrl: `${window.location.origin}/customer/dashboard?status=${AUTHORIZATION_STATUS.CARD_SUCESS}`,
			failedRedirectUrl: `${window.location.origin}/customer/dashboard?status=${AUTHORIZATION_STATUS.CARD_FAILURE}`,
		})
			.unwrap()
			.then(res => (window.location.href = res?.authorizationUrl))
			.catch(err => {
				toast.error(err?.data?.title as string);
				dispatch(hideLoader());
			});
		// .finally(() => );
	};

	const handleInitiateDebitMandate = () => {
		if (!isAcceptTerms) {
			toast.warning("You have to accept the terms");
			return;
		}
		dispatch(showLoader());
		triggerDebitMandate({ redirectUrl: `${window.location.origin}/customer/dashboard`, orderId })
			.unwrap()
			.then(res => (window.location.href = res?.monoUrl))
			.catch(err => {
				toast.error(err?.data?.title as string);
				dispatch(hideLoader());
			});
		// .finally(() => dispatch(hideLoader()));
	};

	const handleCheckMandateConnceted = () => {
		dispatch(showLoader());
		checkMandateStatus(orderId)
			.unwrap()
			.then(res => {
				setIsCardAlreadyConnected(res?.paystackConnected);
				setIsMonoAlreadyConnected(res?.monoDirectDebitConnected);
			})
			.finally(() => dispatch(hideLoader()));
	};

	const toggleDialog = () => {
		setShowConfirm(!showConfirm);
	};

	const canProceed = useMemo(() => {
		return isAcceptTerms && (isConnectCard || isCardAlreadyConnected);
	}, [isAcceptTerms, isConnectCard, isConnectMono]);

	return (
		<>
			{showConfirm && (
				<ConfirmationDialog
					title="Are you sure you want to cancel authorization?"
					description="Kindly confirm this action"
					actionLoading={false}
					onConfirm={() => {
						navigate("/customer/dashboard");
						setView("default");
					}}
					onCloseDialog={toggleDialog}
					btnText="Proceed"
				/>
			)}
			<div className={styles.container}>
				<div className={styles.wrapper}>
					<div className={styles.header}>
						<Button rightIcon={<ColoredChevronLeft />} title="Cancel" style={ButtonStyle.WHITE} onClick={toggleDialog} />
						<div className={styles.title}>
							{/* <div className={styles.titleHeader}>Congratulations!</div> */}
							<span className={styles.subTitle}>Before you make a deposit please provide the following information for us</span>
						</div>
						<Button
							disbaled={!canProceed}
							style={ButtonStyle.DARK}
							title="Save & Continue"
							leftIcon={<ChevronRight />}
							onClick={() => setView("initialDeposit")}
						/>
					</div>
					<div>
						<p className={`${styles.term} ${styles.mb}`}>
							Please be aware that the information we collect is secure and will only be used in the event of a payment default.{" "}
						</p>
						<div className={`${styles.term} ${styles.checkLine}`}>
							<CommonCheckBox className={styles.authPaymentCheckBox} isVisible selected={isAcceptTerms} onToggle={() => setIsAcceptTerms(!isAcceptTerms)} />{" "}
							<span className={styles.term}>
								I have read and accept the&nbsp;
									<Link to={ApplicationRoutes.TERMS} className={`${styles.sectionItem} ${styles.noBorder}`}>
										<span className={styles.emphasis}>Terms</span> and <span className={styles.emphasis}>Conditions</span>
									</Link>
							</span>
						</div>
					</div>
					<div className={styles.buttonSection}>
						<div className={styles.buttonTitle}>Alternative Card Payment Details</div>
						<div className={styles.buttonLabel}>Please provide a valid debit card, that can be debited when your other payments options are not available</div>
						<Button
							disbaled={isConnectCard || isCardAlreadyConnected}
							onClick={handleInitateCardTransaction}
							title="Connect Cards"
							style={ButtonStyle.LONG}
							leftIcon={!(isConnectCard || isCardAlreadyConnected) ? <></> : <GreenTick style={{ marginLeft: 10 }} />}
						/>
					</div>
					<div className={styles.buttonSection}>
						<div className={styles.buttonTitle}>Direct Debit for your Bank Account(Optional)</div>
						<div className={styles.buttonLabel}>This gives us recurring authorization to debit you for the repayment for your solar system</div>
						<Button
							onClick={handleInitiateDebitMandate}
							rightIcon={<MonoLogo />}
							title="Authorize Direct Debit with Mono"
							leftIcon={!(isConnectMono || isMonoAlreadyConnected) ? <></> : <GreenTick style={{ marginLeft: 10 }} />}
							style={ButtonStyle.LONG}
							disbaled={isConnectMono || isMonoAlreadyConnected}
						/>
					</div>
				</div>
				<div className={styles.bottomControl}>
					<Button rightIcon={<ColoredChevronLeft />} title="Cancel" style={ButtonStyle.WHITE} onClick={toggleDialog} />
					<Button
						disbaled={!canProceed}
						style={ButtonStyle.DARK}
						title="Save & Continue"
						leftIcon={<ChevronRight />}
						onClick={() => setView("initialDeposit")}
					/>
				</div>
			</div>
		</>
	);
};
export default AuthorizePaymentMethod;
